import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  isIntegrationStyleFunnel,
  shouldIncludeLegacyNullFunnel,
} from "@/lib/deals-funnel-filter";
import type { SalesFunnel } from "@/types/lead";

export type DealsFunnelLeadPlaceholder = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
};

type KanbanLeadRow = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
};

export function useDealsFunnelLeadsWithoutDeal(
  workspaceId: string | null,
  funnelId: string | null,
  funnel: SalesFunnel | undefined,
  dealLeadIdsWithKanbanDeal: ReadonlySet<string>,
) {
  const isDefaultFunnel = !!funnel?.is_default;
  const useMembership = isIntegrationStyleFunnel(funnel);
  const includeLegacy = shouldIncludeLegacyNullFunnel(funnelId, isDefaultFunnel);

  return useQuery({
    queryKey: [
      "deals-funnel-leads-without-deal",
      workspaceId,
      funnelId,
      useMembership,
      includeLegacy,
      [...dealLeadIdsWithKanbanDeal].sort().join(","),
    ],
    queryFn: async (): Promise<DealsFunnelLeadPlaceholder[]> => {
      if (!workspaceId || !funnelId) return [];

      const { data, error } = await supabase.rpc("fetch_kanban_leads", {
        p_workspace_id: workspaceId,
        p_funnel_id: funnelId,
        p_include_null_funnel: includeLegacy,
        p_per_stage: 80,
        p_is_integration_funnel: useMembership,
        p_search: undefined,
      });
      if (error) throw error;

      const payload = data as { leads?: KanbanLeadRow[] } | null;
      const rawLeads = payload?.leads ?? [];

      return rawLeads
        .filter(lead => !dealLeadIdsWithKanbanDeal.has(String(lead.id)))
        .map(lead => ({
          id: String(lead.id),
          name: lead.name,
          phone: lead.phone,
          email: lead.email,
        }));
    },
    enabled: !!workspaceId && !!funnelId,
    staleTime: 30_000,
  });
}
